import {
    CreateButton,
    DataTable,
    DateField,
    ExportButton,
    List,
    ReferenceField,
    SearchInput,
    TextField,
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { useRecordContext } from 'ra-core';
import { StatusBadge } from '../documents/shared/StatusBadge';
import { TopToolbar } from '../layout/TopToolbar';

const PriorityBadge = () => {
    const record = useRecordContext();
    if (!record?.priority) return null;
    const colors: Record<string, string> = {
        low: 'bg-muted text-muted-foreground border-border',
        medium: 'bg-info/10 text-info border-info/30',
        high: 'bg-orange-50 text-orange-700 border-orange-200',
        urgent: 'bg-destructive/10 text-destructive border-destructive/30',
    };
    return (
        <Badge variant="outline" className={`text-[10px] font-bold uppercase ${colors[record.priority] || ''}`}>
            {record.priority}
        </Badge>
    );
};

const QABadge = () => {
    const record = useRecordContext();
    if (!record?.qa_status) return <span className="text-xs text-muted-foreground">—</span>;
    const colors: Record<string, string> = {
        pending: 'bg-warning/10 text-warning border-warning/30',
        passed: 'bg-success/10 text-success border-success/30',
        failed: 'bg-destructive/10 text-destructive border-destructive/30',
    };
    return (
        <Badge variant="outline" className={`text-[10px] font-bold uppercase ${colors[record.qa_status] || ''}`}>
            {record.qa_status}
        </Badge>
    );
};

const StatusCell = () => {
    const record = useRecordContext();
    if (!record) return null;
    return <StatusBadge status={record.status} />;
};

const WorkOrderListActions = () => (
    <TopToolbar>
        <ExportButton />
        <CreateButton label="New Work Order" />
    </TopToolbar>
);

const filters = [
    <SearchInput key="q" source="q" alwaysOn />,
];

export const WorkOrderList = () => (
    <List
        title="Work Orders"
        actions={<WorkOrderListActions />}
        filters={filters}
        sort={{ field: 'created_at', order: 'DESC' }}
        className="p-6"
    >
        <Card className="border-none shadow-sm overflow-hidden">
            <DataTable rowClick="show">
                <DataTable.Col
                    source="reference"
                    label="Work Order No."
                    className="font-mono font-bold text-foreground"
                />
                <DataTable.Col label="Project">
                    <ReferenceField source="project_id" reference="projects" link={false}>
                        <TextField source="name" className="font-medium text-primary" />
                    </ReferenceField>
                </DataTable.Col>
                <DataTable.Col label="Priority">
                    <PriorityBadge />
                </DataTable.Col>
                <DataTable.Col label="Status">
                    <StatusCell />
                </DataTable.Col>
                <DataTable.Col label="QA">
                    <QABadge />
                </DataTable.Col>
                <DataTable.Col label="Assigned">
                    <ReferenceField source="assigned_to" reference="sales" link={false}>
                        <TextField source="first_name" className="text-sm font-medium" />
                    </ReferenceField>
                </DataTable.Col>
                <DataTable.Col label="Start">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                        <Calendar className="w-3 h-3" />
                        <DateField source="start_date" />
                    </div>
                </DataTable.Col>
            </DataTable>
        </Card>
    </List>
);
